/**
 * Role / access helpers — prefer GET /api/account/access; fall back to login user fields.
 */

const ROLE_KEY = "omni_watch_role";
const ACCESS_KEY = "account_access";

function moduleCrud(access, key) {
  const m = access?.modules?.[key];
  if (!m || typeof m !== "object") return null;
  return m;
}

function hasModuleAccessMap(access) {
  const modules = access?.modules;
  return Boolean(modules && typeof modules === "object" && Object.keys(modules).length > 0);
}

/** Whether the signed-in user may read a dashboard module (from account access). */
export function canReadModule(access, moduleKey) {
  if (!access || typeof access !== "object") return false;
  if (access.isAdmin) return true;
  const m = moduleCrud(access, moduleKey);
  if (m) return Boolean(m.read);
  return false;
}

/** Module read with legacy boolean flags when `modules` is not yet populated. */
export function canReadModuleOrLegacy(access, moduleKey) {
  if (!access || typeof access !== "object") return false;
  if (access.isAdmin) return true;
  if (hasModuleAccessMap(access)) return canReadModule(access, moduleKey);
  if (moduleKey === "cameras") {
    return Boolean(access.canCreateCameras);
  }
  if (moduleKey === "users" || moduleKey === "roles") {
    return Boolean(access.canManageUsers || access.canManageRoles);
  }
  if (moduleKey === "ai_models" || moduleKey === "rules" || moduleKey === "reports") {
    return Boolean(access.canManageUsers || access.canManageRoles);
  }
  return false;
}

/** Whether the signed-in user may create in a module. */
export function canCreateInModule(access, moduleKey) {
  if (!access || typeof access !== "object") return false;
  if (access.isAdmin) return true;
  const m = moduleCrud(access, moduleKey);
  if (m) return Boolean(m.create);
  if (!hasModuleAccessMap(access)) {
    if (moduleKey === "users" || moduleKey === "roles") {
      return Boolean(access.canManageUsers || access.canManageRoles);
    }
  }
  return false;
}

/** Whether the signed-in user may update in a module. */
export function canUpdateInModule(access, moduleKey) {
  if (!access || typeof access !== "object") return false;
  if (access.isAdmin) return true;
  const m = moduleCrud(access, moduleKey);
  if (m) return Boolean(m.update);
  if (!hasModuleAccessMap(access)) {
    if (moduleKey === "users" || moduleKey === "roles") {
      return Boolean(access.canManageUsers || access.canManageRoles);
    }
  }
  return false;
}

/** Whether the signed-in user may delete in a module. */
export function canDeleteInModule(access, moduleKey) {
  if (!access || typeof access !== "object") return false;
  if (access.isAdmin) return true;
  const m = moduleCrud(access, moduleKey);
  if (m) return Boolean(m.delete);
  if (!hasModuleAccessMap(access)) {
    if (moduleKey === "users" || moduleKey === "roles") {
      return Boolean(access.canManageUsers || access.canManageRoles);
    }
  }
  return false;
}

/** CRUD flags for a module (from GET /api/account/access). */
export function getModuleCrud(access, moduleKey) {
  return {
    read: canReadModuleOrLegacy(access, moduleKey),
    create: moduleKey === "cameras" ? canCreateCameras(access) : canCreateInModule(access, moduleKey),
    update: canUpdateInModule(access, moduleKey),
    delete: canDeleteInModule(access, moduleKey),
  };
}

function administrationSectionsMap(access) {
  const s = access?.administrationSections;
  return s && typeof s === "object" ? s : null;
}

/** Single Administration tab/section from GET /api/account/access `administrationSections`. */
export function canAccessAdministrationSection(access, sectionKey) {
  if (!access || typeof access !== "object") return false;
  if (access.isAdmin) return true;
  const sections = administrationSectionsMap(access);
  if (sections) return Boolean(sections[sectionKey]);
  return false;
}

/** Show Administration menu / routes — any section true, or legacy module flags. */
export function canAccessAdministration(access) {
  if (!access || typeof access !== "object") return false;
  if (access.isAdmin) return true;

  const sections = administrationSectionsMap(access);
  if (sections) {
    return Object.values(sections).some(Boolean);
  }

  if (access.canManageUsers || access.canManageRoles) return true;
  const adminModules = ["cameras", "ai_models", "users", "roles", "rules", "use_cases", "zones", "reports"];
  return adminModules.some((key) => canReadModule(access, key));
}

/** Admin / super-admin users do not need per-camera assignment. */
export function isPrivilegedSystemRole(systemRoleLabel) {
  const s = String(systemRoleLabel ?? "")
    .toLowerCase()
    .trim();
  return s === "admin" || s === "super admin" || s === "super_admin" || s === "superadmin";
}

/** Predefined panel admin role in user form (not custom roles named "Admin"). */
export function isPrivilegedRoleOption(roleId, roleOptions) {
  if (!roleId || !Array.isArray(roleOptions)) return false;
  const opt = roleOptions.find((o) => o.id === roleId);
  if (!opt) return false;
  if (opt.isPredefined === false) return false;
  if (opt.systemRole) return isPrivilegedSystemRole(opt.systemRole);
  if (opt.slug) return isPrivilegedSystemRole(opt.slug);
  return false;
}

export function canCreateCameras(access) {
  if (!access || typeof access !== "object") return false;
  if (access.isAdmin) return true;
  if (canCreateInModule(access, "cameras")) return true;
  return Boolean(access.canCreateCameras);
}

export function isSystemAdmin(access) {
  return Boolean(access?.isAdmin);
}

/** @deprecated Prefer account access; kept for legacy user object checks. */
export function isAdminUser(user, access) {
  if (access && typeof access === "object") return Boolean(access.isAdmin);
  if (!user || typeof user !== "object") return false;
  const r = String(user.role ?? user.user_type ?? user.userType ?? "")
    .toLowerCase()
    .trim();
  if (r === "admin") return true;
  if (user.is_admin === true || user.isAdmin === true) return true;
  return false;
}

export function getAppRoleFromAccess(access) {
  if (access?.isAdmin) return "admin";
  return "user";
}

export function getAppRoleFromUser(user, access) {
  if (access) return getAppRoleFromAccess(access);
  return isAdminUser(user) ? "admin" : "user";
}

export function persistAccountAccess(access) {
  try {
    if (access) {
      sessionStorage.setItem(ACCESS_KEY, JSON.stringify(access));
    } else {
      sessionStorage.removeItem(ACCESS_KEY);
    }
  } catch {
    /* ignore */
  }
}

export function loadAccountAccess() {
  try {
    const raw = sessionStorage.getItem(ACCESS_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function syncAppRoleFromAccess(access) {
  try {
    sessionStorage.setItem(ROLE_KEY, getAppRoleFromAccess(access));
  } catch {
    /* ignore */
  }
}

export function syncAppRoleFromUser(user, access) {
  try {
    sessionStorage.setItem(ROLE_KEY, getAppRoleFromUser(user, access));
  } catch {
    /* ignore */
  }
}

export function clearAppRole() {
  try {
    sessionStorage.removeItem(ROLE_KEY);
    sessionStorage.removeItem(ACCESS_KEY);
  } catch {
    /* ignore */
  }
}
